import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { spacesApi } from '../api';

// Fase 5.7 — invalidar la lista de miembros de un space desde un componente presentacional
// (que no puede importar `@tanstack/react-query`). Antes `ComponentSpaceDashboard.jsx` leía
// `useQueryClient()` directo para hacer esto en 3 `onSuccess`.
export function useInvalidateSpaceMembers() {
    const qc = useQueryClient();
    return useCallback((spaceId) => qc.invalidateQueries({ queryKey: ['space', spaceId, 'members'] }), [qc]);
}

export function useSpaceStats(spaceId) {
    return useQuery({
        queryKey: ['space', spaceId, 'stats'],
        queryFn: () => spacesApi.fetchStats(spaceId),
        enabled: !!spaceId,
    });
}

export function useSpaceActivity(spaceId) {
    return useQuery({
        queryKey: ['space', spaceId, 'activity'],
        queryFn: () => spacesApi.fetchActivity(spaceId),
        enabled: !!spaceId,
    });
}

// Utiliza el nuevo endpoint del backend que ya hace el JOIN correcto entre SpaceMember y User,
// en lugar de depender del blob JSON obsoleto `space.members` y cruzarlo en el frontend.
export function useSpaceMembers(space) {
    return useQuery({
        queryKey: ['space', space?.id, 'members'],
        queryFn: async () => {
            if (!space?.id) return [];
            const membersData = await spacesApi.fetchMembers(space.id);
            
            return membersData.map(sm => ({
                id: sm.id,
                slot: sm.id,
                role: sm.role || 'member',
                status: sm.status || 'active',
                name: sm.name || `User ${sm.id}`,
                email: sm.email || `user${sm.id}@colonny.xyz`,
                avatar: sm.image || `https://ui-avatars.com/api/?name=${sm.name || 'User'}&background=random`,
                permissions: sm.permissions || {},
                is_agent: !!sm.is_agent || sm.role === 'agent',
                globalRoles: [] // Backend could be updated to send this if needed
            }));
        },
        enabled: !!space?.id,
    });
}

export function useCreateSpaceAgent(spaceId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => spacesApi.createAgent(spaceId, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['space', spaceId, 'members'] });
            qc.invalidateQueries({ queryKey: ['agents'] });
        },
    });
}

export function useUpdateSpace() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }) => spacesApi.update(id, payload),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: ['space', id] });
        },
    });
}

export function useUploadSpaceLogo() {
    return useMutation({
        mutationFn: (file) => spacesApi.uploadLogo(file),
    });
}
export const getDefaultPermission = (roleStr, key) => {
    const role = (roleStr || 'colaborador').toLowerCase();
    const globallyInactive = ['wa_connect', 'wa_auto', 'wa_third_party', 'task_delete'];
    if (globallyInactive.includes(key)) return false;

    const isHighPriv = role.includes('admin') || role.includes('owner');
    const isLowPriv = role.includes('broker') || role.includes('cliente');

    if (isHighPriv) {
        if (key === 'map_edit' && (role === 'sub admin' || role === 'sub-admin')) return false;
        return true;
    }

    if (isLowPriv) {
        const allowed = ['map_view', 'map_mic', 'map_cam', 'map_screen'];
        return allowed.includes(key);
    }

    // Default for colaborador
    const inactiveForCollab = ['nav_database', 'doc_share', 'wa_download', 'map_see_others', 'map_notes', 'map_edit'];
    if (inactiveForCollab.includes(key)) return false;
    return true;
};

export function useRemoveSpaceMember() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ spaceId, userId }) => spacesApi.removeMember(spaceId, userId),
        onSuccess: (_, { spaceId }) => {
            qc.invalidateQueries({ queryKey: ['space', spaceId, 'members'] });
        }
    });
}

export function useCurrentUserPermissions() {
    // Note: To avoid circular dependency with UserContext if needed, we'll just parse localStorage or use the hook if available
    const userJson = localStorage.getItem('currentUser');
    const user = userJson ? JSON.parse(userJson) : null;
    const spaceId = user?.currentSpace?.id;
    const { data: members = [], isLoading } = useSpaceMembers({ id: spaceId });
    
    if (!spaceId || !user?.id) return { permissions: {}, isLoading: false, role: 'colaborador' };
    const member = members.find(m => String(m.id) === String(user.id));
    return { permissions: member?.permissions || {}, isLoading, role: member?.role || 'colaborador' };
}

export function useUserProfile(userId) {
    return useQuery({
        queryKey: ['user', userId, 'profile'],
        queryFn: () => spacesApi.fetchUserProfile(userId),
        enabled: !!userId,
    });
}

// Movidos desde ComponentSpaceDashboard.jsx (02.8-spaces-mail-shell.md §4/§0.1) -- ver el
// comentario de `spacesApi.suspendMember`/`createDefaultDoc` (spaces/api.js) para el porqué de
// la migración. Sin invalidación de query: ninguno de los 2 alimenta una lista cacheada que este
// hook conozca (la suspensión no cambia `useSpaceMembers`' shape hoy; `createDefaultDoc` navega
// directo tras resolver, el callback `onSpaceUpdate` del propio componente sigue siendo lo que
// refresca el `space` del padre).
export function useSuspendSpaceMember() {
    return useMutation({
        mutationFn: (userId) => spacesApi.suspendMember(userId),
    });
}

export function useCreateDefaultDoc() {
    return useMutation({
        mutationFn: (spaceId) => spacesApi.createDefaultDoc(spaceId),
    });
}
